import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Route,
  Switch,
  useLocation,
} from "react-router-dom";
import { CssBaseline, ThemeProvider } from "@mui/material";
import { Global } from "@emotion/react";
// import theme from "./theme";
import { CustomThemeProvider, useCustomTheme } from "./theme";
import { Helmet } from "react-helmet";
import "@aws-amplify/ui-react/styles.css";
import DeveloperLogin from "./auth/developer-login";
import { useRecoilState } from "recoil";
import OAuthCallback from "./oauth-callback";
import { WebSocketProvider } from "./ws/websocket";
import { EventProvider } from "./ws/event-context";
import UserApp from "./views/user-app";
import { ToastProvider } from "./common/toast";
import { BackdropProvider } from "./common/backdrop";
import UserLogin from "./auth/user-login";
import UserLoginCallback from "./auth/user-login-callback";
import {
  linkOrgIdState,
  linkOrgUserIdState,
  platformState,
  userIdTokenState,
} from "./global-state";
import UserLink from "./auth/user-link";
import DeveloperLogout from "./auth/developer-logout";
import UserLogout from "./auth/user-logout";
import DeveloperLoginCallback from "./auth/developer-login-callback";
import Doc from "./setting/doc";
import PaymentSuccess from "./views/payment-success";
import InitScope from "./menu/init-scope";
import WaitlistPage from "./views/waitlist-page";
import { AuthProvider } from "./auth/auth-provider";
import DesktopLogin from "./auth/desktop-login";
import DesktopAuthWait from "./auth/desktop-auth-wait";
import DesktopAuthDone from "./auth/desktop-auth-done";

const ElectronService = {
  isElectronApp: () => window.electron && window.electron.ipcRenderer,

  initializeElectronListeners: async () => {
    if (!ElectronService.isElectronApp()) {
      console.log("Not an Electron app, returning");
      return;
    }
  },

  getPCInfo: async () => {
    try {
      const result = await window.electron.ipcRenderer.invoke("get-pc-info");
      return result;
    } catch (error) {
      console.error("Error getting PC info:", error);
      return null;
    }
  },

  getIdToken: async () => {
    try {
      const result = await window.electron.ipcRenderer.invoke(
        "auth:get-id-token"
      );
      return result;
    } catch (error) {
      console.error("Error getting PC info:", error);
      return null;
    }
  },
};

const PrivateRoute = ({ component: Component, authed, ...rest }) => (
  <Route {...rest} render={(props) => <Component {...props} />} />
);

// export default function App() {
const AppContent = () => {
  const [userIdToken, setUserIdToken] = useRecoilState(userIdTokenState);
  const [linkOrgId, setLinkOrgId] = useRecoilState(linkOrgIdState);
  const [linkOrgUserId, setLinkOrgUserId] = useRecoilState(linkOrgUserIdState);
  const [platform, setPlatform] = useRecoilState(platformState);

  const { theme } = useCustomTheme();

  const getSubtitle = () => {
    switch (process.env.REACT_APP_ENVIRONMENT) {
      case "staging":
        return "mHome AI user app - staging";
      default:
        return "mHome AI user app";
    }
  };

  useEffect(() => {
    const initializePlatform = async () => {
      const p = { source: "", config: {} };
      if (window.Android) {
        console.log("From Android!");

        window.syncAppData = () => {
          const token = window.Android.getIdToken();
          if (token) {
            setUserIdToken(token);
          }
        };

        const token = window.Android.getIdToken();
        if (token) {
          setUserIdToken(token);
        }
        const orgId = window.Android.getOrgId();
        const orgUserId = window.Android.getOrgUserId();
        if (orgId && orgUserId) {
          setLinkOrgId(orgId);
          setLinkOrgUserId(orgUserId);
        }
        p.source = "app";
        // Check if the domain matches and store `localServiceAddress` in `localStorage`
        const localServiceAddress = window.Android.getLocalServiceAddress();
        if (localServiceAddress) {
          localStorage.setItem("localServiceAddress", localServiceAddress);
        }
      } else if (ElectronService.isElectronApp()) {
        p.source = "desktop";

        // Initialize Electron listeners
        await ElectronService.initializeElectronListeners();

        // Get PC info separately
        try {
          const pcInfo = await ElectronService.getPCInfo();
          if (pcInfo) {
            const desktopInfo = {
              deviceId: pcInfo.machineId,
              deviceName: pcInfo.hostname,
            };
            p.info = desktopInfo;
          }
        } catch (error) {
          console.error("Error getting PC info:", error);
        }
      } else {
        p.source = "web";
      }
      console.log("Platform:", p);
      setPlatform(p);
    };

    initializePlatform();
  }, [setUserIdToken, setLinkOrgId, setLinkOrgUserId, setPlatform]);

  // Add listener for token updates from the main process
  useEffect(() => {
    if (ElectronService.isElectronApp()) {
      const initializeToken = async () => {
        const token = await ElectronService.getIdToken();
        if (token) {
          setUserIdToken(token);
        }
      };

      initializeToken();

      const handleTokenUpdate = async (newToken) => {
        if (newToken) {
          setUserIdToken(newToken);
        }
      };

      const handleAuthError = (errorData) => {
        console.log("Received auth error from main process:", errorData);
        // Redirect to login page when authentication errors occur
        if (
          window.location.pathname !== "/user-login" &&
          window.location.pathname !== "/desktop-auth-wait"
        ) {
          window.location.href = "/user-login";
        }
      };

      // Add event listeners
      window.electron.ipcRenderer.on("auth:tokens-updated", handleTokenUpdate);
      window.electron.ipcRenderer.on("auth:error", handleAuthError);

      // Clean up event listeners on component unmount
      return () => {
        window.electron.ipcRenderer.removeListener(
          "auth:tokens-updated",
          handleTokenUpdate
        );
        window.electron.ipcRenderer.removeListener(
          "auth:error",
          handleAuthError
        );
      };
    }
  }, [setUserIdToken]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <GlobalScrollbarStyles />
      <Helmet>
        <title>mHome</title>
        <meta name="description" content={getSubtitle()} />
      </Helmet>
      <Router basename="/">
        <AppRoutes platform={platform} />
      </Router>
    </ThemeProvider>
  );
};

const AppRoutes = ({ platform }) => {
  const location = useLocation();

  const shouldRenderAuthProvider = () => {
    const nonAuthPaths = [
      "/terms-of-use",
      "/privacy-policy",
      "/licenses",
      "/developer-login",
      "/developer-logout",
      "/user-login",
      "/user-logout",
      "/user-link",
      "/user-login-callback",
      "/developer-login-callback",
    ];
    return !nonAuthPaths.includes(location.pathname);
  };

  return (
    <ToastProvider>
      <BackdropProvider>
        {platform && (
          <>
            <Switch>
              <Route exact path="/terms-of-use">
                <Doc docName={"terms_of_use"} />
              </Route>
              <Route exact path="/privacy-policy">
                <Doc docName={"privacy_policy"} />
              </Route>
              <Route exact path="/licenses">
                <Doc docName={"licenses"} />
              </Route>
              <Route exact path="/developer-login">
                <DeveloperLogin />
              </Route>
              <Route exact path="/developer-logout">
                <DeveloperLogout />
              </Route>
              <Route exact path="/user-login">
                <UserLogin />
              </Route>
              <Route exact path="/user-logout">
                <UserLogout />
              </Route>
              <Route exact path="/user-link">
                <UserLink />
              </Route>
              <Route exact path="/user-login-callback">
                <UserLoginCallback />
              </Route>
              <Route exact path="/developer-login-callback">
                <DeveloperLoginCallback />
              </Route>
            </Switch>
            {shouldRenderAuthProvider() && (
              <AuthProvider>
                <EventProvider>
                  <WebSocketProvider>
                    <Switch>
                      <Route exact path="/desktop-login">
                        <DesktopLogin />
                      </Route>
                      <Route exact path="/desktop-auth-wait">
                        <DesktopAuthWait />
                      </Route>
                      <Route exact path="/desktop-auth-done">
                        <DesktopAuthDone />
                      </Route>
                      <Route exact path="/oauth-callback">
                        <OAuthCallback />
                      </Route>
                      <Route exact path="/init-scope">
                        <InitScope />
                      </Route>
                      <Route exact path="/waitlist">
                        <WaitlistPage />
                      </Route>
                      <Route exact path="/payment-success">
                        <PaymentSuccess />
                      </Route>
                      <PrivateRoute path="/" component={UserApp} />
                    </Switch>
                  </WebSocketProvider>
                </EventProvider>
              </AuthProvider>
            )}
          </>
        )}
      </BackdropProvider>
    </ToastProvider>
  );
};

export default function App() {
  return (
    <CustomThemeProvider>
      <AppContent />
    </CustomThemeProvider>
  );
}

const GlobalScrollbarStyles = () => (
  <Global
    styles={{
      "::-webkit-scrollbar": {
        width: "4px",
        background: "transparent", // Track color
      },
      "::-webkit-scrollbar-track": {
        background: "transparent", // Track color
      },
      "::-webkit-scrollbar-thumb": {
        backgroundColor: "rgba(0, 0, 0, 0.2)", // Adjust color as needed
        borderRadius: "4px", // Optional: round the scrollbar thumb
      },
      "::-webkit-scrollbar-thumb:hover": {
        background: "rgba(0, 0, 0, 0.5)", // Thumb color on hover
      },
      scrollbarWidth: "thin", // Firefox 68+
      scrollbarColor: "rgba(0, 0, 0, 0.2) transparent", // Firefox 68+
    }}
  />
);
